import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { config } from '../config';
import './BookTickets.css';

interface AvailableTicket {
  ticketType: string;
  eventType: string;
  pricePerTicket: number;
  totalAvailable: number;
  eventDate: string | null;
  description?: string | null;
  venue?: string | null;
}

interface CartItem extends AvailableTicket {
  quantity: number;
}

const TICKET_COLORS: Record<string, string> = {
  premium: 'var(--ticket-premium)',
  basic: 'var(--ticket-basic)',
  normal: 'var(--ticket-normal)',
};

const getTicketDetails = (ticketType: string) => {
  switch (ticketType.toLowerCase()) {
    case 'normal':
      return (
        <div className="bt-ticket-row-details">
          {/* <p>Stage view from the back side (only standing)</p>
          <p>No redeemable coupons available.</p> */}
        </div>
      );
    case 'basic':
      return (
        <div className="bt-ticket-row-details">
          {/* <p>Stage view from the center (only standing)</p>
          <p>15,000 redeemable coupons available.</p> */}
        </div>
      );
    case 'premium':
      return (
        <div className="bt-ticket-row-details">
          {/* <p>Stage view from the front side (chairs, sofas, and tables)</p>
          <p>Fully redeemable coupons available.</p> */}
        </div>
      );
    default:
      return null;
  }
};

const TICKET_ICONS: Record<string, string> = {
  premium: '💎',
  basic: '⭐',
  normal: '🎟️',
};

const LOW_STOCK_THRESHOLD = 3;

export function BookTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<AvailableTicket[]>([]);

  // Initialize cart from sessionStorage for page navigation persistence
  const [cart, setCart] = useState<Record<string, number>>(() => {
    try {
      const saved = sessionStorage.getItem('luminar_cart');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Persist cart selection
  useEffect(() => {
    sessionStorage.setItem('luminar_cart', JSON.stringify(cart));
  }, [cart]);

  // Expand / collapse state for events
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    musical: true,
    techno: true,
  });

  const toggleExpand = (event: string) => {
    setExpanded((prev) => ({
      ...prev,
      [event]: !prev[event],
    }));
  };

  const cartKey = (t: AvailableTicket) => `${t.ticketType}__${t.eventType}__${t.pricePerTicket}__${t.eventDate || ''}__${t.venue || ''}`;

  const fetchAvailable = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${config.API_BASE_URL}/tickets/available`);
      if (!res.ok) throw new Error('Failed to load tickets');
      const data: AvailableTicket[] = await res.json();
      setTickets(data);
    } catch {
      setError('Could not load available tickets. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailable();
  }, [fetchAvailable]);

  const updateCart = (ticket: AvailableTicket, delta: number) => {
    const key = cartKey(ticket);
    setCart((prev) => {
      const current = prev[key] || 0;
      const next = Math.min(ticket.totalAvailable, Math.max(0, current + delta));
      return { ...prev, [key]: next };
    });
  };

  const getQty = (ticket: AvailableTicket) => cart[cartKey(ticket)] || 0;

  const cartItems: CartItem[] = tickets
    .map((t) => ({ ...t, quantity: getQty(t) }))
    .filter((t) => t.quantity > 0);

  const grandTotal = cartItems.reduce(
    (sum, t) => sum + t.pricePerTicket * t.quantity,
    0,
  );

  const hasItems = cartItems.length > 0;

  const handleProceedToCheckout = () => {
    if (!hasItems) return;
    navigate('/order-summary', {
      state: {
        cartItems,
        grandTotal,
      },
    });
  };


  const musicalTickets = tickets.filter((t) => t.eventType.toLowerCase() === 'musical');
  const technoTickets = tickets.filter((t) => t.eventType.toLowerCase() === 'techno');

  const hasMusical = musicalTickets.length > 0;
  const hasTechno = technoTickets.length > 0;

  const musicalEvent = musicalTickets[0] || null;
  const technoEvent = technoTickets[0] || null;

  return (
    <div className="bt-page">
      {/* Ambient background */}
      <div className="bt-bg">
        <div className="bt-bg__orb bt-bg__orb--1" />
        <div className="bt-bg__orb bt-bg__orb--2" />
        <div className="bt-bg__grid" />
      </div>

      <div className="bt-container">
        {/* Header */}
        <header className="bt-header">
          <Link to="/" className="bt-back">← Back</Link>
          <div className="bt-header__text">
            <span className="bt-eyebrow">Luminar Events</span>
            <h1 className="bt-title">Book <span className="bt-title__accent">Tickets</span></h1>
            <p className="bt-subtitle">
              Select your tickets below, choose your quantity, and proceed to secure payment.
            </p>
          </div>
        </header>

        {/* Unified Booking Card */}
        <div className="bt-booking-card">
          {/* Left Side: Ticket Selection */}
          <div className="bt-booking-tickets">
            {loading ? (
              <div className="bt-loading">
                <div className="bt-spinner" />
                <span>Loading available tickets…</span>
              </div>
            ) : error ? (
              <div className="bt-error">
                <span>{error}</span>
                <button onClick={fetchAvailable} className="bt-retry">Retry</button>
              </div>
            ) : (
              <>
                {/* ── Musical Event Card ───────────────────────────────────── */}
                <div className={`bt-event-group ${hasMusical ? 'bt-event-group--open' : 'bt-event-group--closed'}`}>
                  <div
                    className={`bt-event-header-card ${!hasMusical ? 'bt-event-header-card--disabled' : ''}`}
                    onClick={() => hasMusical && toggleExpand('musical')}
                  >
                    <div className="bt-event-banner-wrap">
                      <img src="/images/musical_banner.png" alt="Musical Event Banner" className="bt-event-banner-img" />
                      <div className="bt-event-banner-overlay" />
                    </div>
                    <div className="bt-event-header-details">
                      <div className="bt-event-badges-row">
                        <span className="bt-event-badge-genre musical-badge">MUSICAL</span>
                        {hasMusical ? (
                          <span className="bt-event-status-badge open">Booking Open</span>
                        ) : (
                          <span className="bt-event-status-badge closed">No Tickets</span>
                        )}
                      </div>
                      <h2 className="bt-event-group-title">Luminar Musical &amp; Orchestra</h2>
                      <div className="bt-event-group-meta">
                        {hasMusical && musicalEvent?.eventDate ? (
                          <span className="bt-meta-item">
                            📅 {new Date(musicalEvent.eventDate).toLocaleDateString('en-IN', {
                              weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                            })} at {new Date(musicalEvent.eventDate).toLocaleTimeString('en-IN', {
                              hour: '2-digit', minute: '2-digit', hour12: true,
                            })}
                          </span>
                        ) : (
                          <span className="bt-meta-item">📅 Stay tuned for next show</span>
                        )}
                      </div>
                      <p className="bt-event-group-desc">
                        {hasMusical && musicalEvent?.description
                          ? musicalEvent.description
                          : 'Experience the ultimate theatrical and orchestral journey of live musicals.'}
                      </p>
                    </div>
                    {hasMusical && (
                      <div className="bt-event-toggle-icon">
                        {expanded.musical ? '▲' : '▼'}
                      </div>
                    )}
                  </div>

                  {hasMusical && expanded.musical && (
                    <div className="bt-event-tickets-list">
                      {musicalTickets.map((ticket) => {
                        const qty = getQty(ticket);
                        const subtotal = ticket.pricePerTicket * qty;
                        const color = TICKET_COLORS[ticket.ticketType.toLowerCase()] || '#e10a1e';
                        const icon = TICKET_ICONS[ticket.ticketType.toLowerCase()] || '🎟️';
                        const isLowStock = ticket.totalAvailable > 0 && ticket.totalAvailable <= LOW_STOCK_THRESHOLD;
                        const isSoldOut = ticket.totalAvailable === 0;
                        const atMax = qty >= ticket.totalAvailable;

                        return (
                          <div
                            key={cartKey(ticket)}
                            className={`bt-ticket-row-card ${qty > 0 ? 'bt-ticket-row-card--selected' : ''}`}
                            style={{ '--ticket-accent': color } as React.CSSProperties}
                          >
                            <div className="bt-ticket-row-info">
                              <div className="bt-ticket-row-type-badge" style={{ background: color }}>
                                {icon} {ticket.ticketType.toUpperCase()}
                              </div>
                              <span className="bt-ticket-row-price">₹{ticket.pricePerTicket.toLocaleString('en-IN')}</span>
                              {ticket.venue && (
                                <span className="bt-ticket-row-meta-inline">📍 {ticket.venue}</span>
                              )}
                              {ticket.eventDate && (
                                <span className="bt-ticket-row-meta-inline">📅 {new Date(ticket.eventDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} at {new Date(ticket.eventDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
                              )}
                              {isLowStock && (
                                <span className="bt-ticket-row-stock-warn">🔥 Only {ticket.totalAvailable} left!</span>
                              )}
                              {getTicketDetails(ticket.ticketType)}
                            </div>

                            <div className="bt-ticket-row-actions">
                              <div className={`bt-counter ${isSoldOut ? 'bt-counter--disabled' : ''}`}>
                                <button
                                  type="button"
                                  className="bt-counter__btn"
                                  onClick={() => updateCart(ticket, -1)}
                                  disabled={qty === 0 || isSoldOut}
                                >
                                  −
                                </button>
                                <span className="bt-counter__num">{qty}</span>
                                <button
                                  type="button"
                                  className="bt-counter__btn"
                                  onClick={() => updateCart(ticket, 1)}
                                  disabled={atMax || isSoldOut}
                                >
                                  +
                                </button>
                              </div>
                              {qty > 0 && (
                                <div className="bt-ticket-row-subtotal">
                                  ₹{subtotal.toLocaleString('en-IN')}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ── Techno Event Card ────────────────────────────────────── */}
                <div className={`bt-event-group ${hasTechno ? 'bt-event-group--open' : 'bt-event-group--closed'}`}>
                  <div
                    className={`bt-event-header-card ${!hasTechno ? 'bt-event-header-card--disabled' : ''}`}
                    onClick={() => hasTechno && toggleExpand('techno')}
                  >
                    <div className="bt-event-banner-wrap">
                      <img src="/images/techno_banner.png" alt="Techno Event Banner" className="bt-event-banner-img" />
                      <div className="bt-event-banner-overlay" />
                    </div>
                    <div className="bt-event-header-details">
                      <div className="bt-event-badges-row">
                        <span className="bt-event-badge-genre techno-badge">TECHNO</span>
                        {hasTechno ? (
                          <span className="bt-event-status-badge open">Booking Open</span>
                        ) : (
                          <span className="bt-event-status-badge closed">No Tickets</span>
                        )}
                      </div>
                      <h2 className="bt-event-group-title">Luminar Techno Club Event</h2>
                      <div className="bt-event-group-meta">
                        {hasTechno && technoEvent?.eventDate ? (
                          <span className="bt-meta-item">
                            📅 {new Date(technoEvent.eventDate).toLocaleDateString('en-IN', {
                              weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                            })} at {new Date(technoEvent.eventDate).toLocaleTimeString('en-IN', {
                              hour: '2-digit', minute: '2-digit', hour12: true,
                            })}
                          </span>
                        ) : (
                          <span className="bt-meta-item">📅 Stay tuned for next event</span>
                        )}
                      </div>
                      <p className="bt-event-group-desc">
                        {hasTechno && technoEvent?.description
                          ? technoEvent.description
                          : 'Deep bass, heavy beats, and electric energy. Experience the finest techno scene.'}
                      </p>
                    </div>
                    {hasTechno && (
                      <div className="bt-event-toggle-icon">
                        {expanded.techno ? '▲' : '▼'}
                      </div>
                    )}
                  </div>

                  {hasTechno && expanded.techno && (
                    <div className="bt-event-tickets-list">
                      {technoTickets.map((ticket) => {
                        const qty = getQty(ticket);
                        const subtotal = ticket.pricePerTicket * qty;
                        const color = TICKET_COLORS[ticket.ticketType.toLowerCase()] || '#e10a1e';
                        const icon = TICKET_ICONS[ticket.ticketType.toLowerCase()] || '🎟️';
                        const isLowStock = ticket.totalAvailable > 0 && ticket.totalAvailable <= LOW_STOCK_THRESHOLD;
                        const isSoldOut = ticket.totalAvailable === 0;
                        const atMax = qty >= ticket.totalAvailable;

                        return (
                          <div
                            key={cartKey(ticket)}
                            className={`bt-ticket-row-card ${qty > 0 ? 'bt-ticket-row-card--selected' : ''}`}
                            style={{ '--ticket-accent': color } as React.CSSProperties}
                          >
                            <div className="bt-ticket-row-info">
                              <div className="bt-ticket-row-type-badge" style={{ background: color }}>
                                {icon} {ticket.ticketType.toUpperCase()}
                              </div>
                              <span className="bt-ticket-row-price">₹{ticket.pricePerTicket.toLocaleString('en-IN')}</span>
                              {ticket.venue && (
                                <span className="bt-ticket-row-meta-inline">📍 {ticket.venue}</span>
                              )}
                              {ticket.eventDate && (
                                <span className="bt-ticket-row-meta-inline">📅 {new Date(ticket.eventDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} at {new Date(ticket.eventDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
                              )}
                              {isLowStock && (
                                <span className="bt-ticket-row-stock-warn">🔥 Only {ticket.totalAvailable} left!</span>
                              )}
                              {getTicketDetails(ticket.ticketType)}
                            </div>

                            <div className="bt-ticket-row-actions">
                              <div className={`bt-counter ${isSoldOut ? 'bt-counter--disabled' : ''}`}>
                                <button
                                  type="button"
                                  className="bt-counter__btn"
                                  onClick={() => updateCart(ticket, -1)}
                                  disabled={qty === 0 || isSoldOut}
                                >
                                  −
                                </button>
                                <span className="bt-counter__num">{qty}</span>
                                <button
                                  type="button"
                                  className="bt-counter__btn"
                                  onClick={() => updateCart(ticket, 1)}
                                  disabled={atMax || isSoldOut}
                                >
                                  +
                                </button>
                              </div>
                              {qty > 0 && (
                                <div className="bt-ticket-row-subtotal">
                                  ₹{subtotal.toLocaleString('en-IN')}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right Side: Order Summary (Ticket Stub Design) */}
          <div className="bt-booking-summary">
            <div className="bt-summary__inner">
              <h2 className="bt-summary__title">Booking Summary</h2>


              {!hasItems ? (
                <div className="bt-summary__empty">
                  <span className="bt-summary__empty-icon">🎟️</span>
                  <span className="bt-summary__empty-text">Select tickets to start booking</span>
                  <span className="bt-summary__empty-sub">Use the + controls on the left to add tickets to your order.</span>
                </div>
              ) : (
                <ul className="bt-summary__list">
                  {cartItems.map((item) => (
                    <li key={cartKey(item)} className="bt-summary__item">
                      <div className="bt-summary__item-info">
                        <span className="bt-summary__item-type">{item.ticketType.toUpperCase()}</span>
                        <span className="bt-summary__item-event">{item.eventType} Event · {item.quantity}×</span>
                      </div>
                      <span className="bt-summary__item-price">
                        ₹{(item.pricePerTicket * item.quantity).toLocaleString('en-IN')}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {hasItems && (
                <>
                  {/* Decorative Ticket Divider */}
                  <div className="bt-summary-divider-wrap">
                    <div className="bt-summary-divider-punch l" />
                    <div className="bt-summary-divider-dash" />
                    <div className="bt-summary-divider-punch r" />
                  </div>

                  <div className="bt-summary__total">
                    <span>Total Amount</span>
                    <span className="bt-summary__total-val">₹{grandTotal.toLocaleString('en-IN')}</span>
                  </div>

                  <button
                    className="bt-pay-btn"
                    onClick={handleProceedToCheckout}
                    disabled={!hasItems}
                  >
                    <span>Proceed to Checkout</span>
                    <span className="bt-pay-btn__arrow">→</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating mobile bottom bar */}
      {hasItems && (
        <div className="bt-mobile-bar">
          <div className="bt-mobile-bar__info">
            <span className="bt-mobile-bar__qty">
              {cartItems.reduce((sum, item) => sum + item.quantity, 0)} ticket{cartItems.reduce((sum, item) => sum + item.quantity, 0) !== 1 ? 's' : ''} selected
            </span>
            <span className="bt-mobile-bar__price">₹{grandTotal.toLocaleString('en-IN')}</span>
          </div>
          <button
            className="bt-mobile-bar__btn"
            onClick={handleProceedToCheckout}
          >
            <span>Proceed to Checkout</span>
            <span className="bt-mobile-bar__arrow">→</span>
          </button>
        </div>
      )}
    </div>
  );
}
